import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import usePageTitle from '../hooks/usePageTitle';
import MarketShell from '../components/market/MarketShell';
import { fetchMarketProductsByIds, fetchRelated, marketImageUrl, marketTitle, variantText } from '../lib/marketCatalog';
import { formatSar } from '../lib/marketPricing';
import { cartShortfall, getCartMethod, onCartChange, priceCart, readCart, removeLine, selectionKey, setCartMethod, setCheckoutSelection, setLineQty } from '../lib/marketCart';
import { fetchMarketState, useDeliveredConfig } from '../lib/marketMode';
import { DELIVERED_T, deliveredLineTotal, deliveredUnit, productPrice, shortDate, staffShipping, useDeliveredMany } from '../lib/marketDelivered';
import { askReason, expressLines, threeOptionCart } from '../lib/threeOptions';
import { cartExpress } from '../lib/marketOrdersApi';
import { CardRow } from './MarketHome';
import PreviewBanner from '../components/market/PreviewBanner';
import { MARKET_API_IS_LOCAL } from '../lib/marketDb';
import SupplierName from '../components/market/SupplierName';
import { track } from '../lib/growthTracker';

// The market cart. Routed only in a REACT_APP_MARKET_PREVIEW build; lines live
// in this browser (lib/marketCart.js);
// every price is recomputed from fresh catalog rows on each visit. Nothing here
// orders, reserves or pays anything.

export const CART_T = {
  ar: {
    title: 'سلة الطلب', empty: 'السلة فارغة.', browse: 'تصفّح المنتجات', back: 'رجوع إلى المنتجات',
    preview: 'بيئة اختبار — الدفع تجريبي.',
    unit: 'سعر الوحدة', lineTotal: 'المجموع', remove: 'حذف', qty: 'الكمية', dec: 'إنقاص', inc: 'زيادة',
    tierByTotal: (n) => `سعر الجملة محسوب على مجموع الكمية من هذا المنتج (${n})`,
    issues: {
      gone: 'المنتج لم يعد متوفرًا — ما يدخل طلبك.', unavailable: 'غير متوفر حاليًا — ما يدخل طلبك.', sku_gone: 'هذا الخيار لم يعد متوفرًا — ما يدخل طلبك.',
      below_moq: (m) => `مجموع هذا المنتج أقل من حدّه الأدنى (${m}).`, step: (s) => `يُطلب بمضاعفات ${s}.`, over_stock: (n) => `أكثر من المتوفر (${n}).`,
    },
    fix: 'تصحيح', priceChanged: (old) => `تغيّر السعر منذ الإضافة (كان ${old}).`,
    summary: 'ملخص الطلب', lines: 'عدد البنود', goods: 'قيمة البضاعة',
    goodsNote: 'في الخطوة التالية نحسب الشحن والجمارك إلى عنوانك، ثم تدفع الإجمالي مرة واحدة.',
    blocking: (n) => `${n} بند يحتاج تصحيح الكمية قبل المتابعة — اضغط «تصحيح» تحت البند.`, dropped: (n) => `${n} منتج غير متوفر حاليًا وما يدخل طلبك. احذفه، أو كمّل بالباقي.`, next: 'متابعة إلى إتمام الطلب', loading: '…',
    minimum: (min, short) => `باقي ${short} وتقدر تكمل طلبك`,
    suggest: 'أكمل طلبك — منتجات قريبة مما في سلتك',
  },
  en: {
    title: 'Order cart', empty: 'Your cart is empty.', browse: 'Browse products', back: 'Back to products',
    preview: 'Test environment — payment is simulated.',
    unit: 'Unit price', lineTotal: 'Total', remove: 'Remove', qty: 'Quantity', dec: 'Decrease', inc: 'Increase',
    tierByTotal: (n) => `Wholesale price based on this product's total quantity (${n})`,
    issues: {
      gone: 'No longer available — left out of your order.', unavailable: 'Currently unavailable — left out of your order.', sku_gone: 'This option is no longer available — left out of your order.',
      below_moq: (m) => `This product's total is below its minimum (${m}).`, step: (s) => `Ordered in multiples of ${s}.`, over_stock: (n) => `More than available (${n}).`,
    },
    fix: 'Fix', priceChanged: (old) => `Price changed since you added it (was ${old}).`,
    summary: 'Order summary', lines: 'Lines', goods: 'Goods value',
    goodsNote: 'Next, we calculate shipping and customs to your address — then you pay one total, once.',
    blocking: (n) => `${n} line(s) need a quantity fix before you continue — tap «Fix» under the line.`, dropped: (n) => `${n} product(s) currently unavailable and left out of your order. Remove them, or continue with the rest.`, next: 'Continue to checkout', loading: '…',
    minimum: (min, short) => `Just ${short} more and you can complete your order`,
    suggest: 'Complete your order — products close to what is in your cart',
  },
  zh: {
    title: '购物车', empty: '购物车是空的。', browse: '浏览商品', back: '返回商品',
    preview: '测试环境 — 付款为模拟。',
    unit: '单价', lineTotal: '小计', remove: '删除', qty: '数量', dec: '减少', inc: '增加',
    tierByTotal: (n) => `批发价按该商品总数量计算（${n}）`,
    issues: {
      gone: '该商品已下架 — 不计入订单。', unavailable: '暂时无货 — 不计入订单。', sku_gone: '该规格已不可用 — 不计入订单。',
      below_moq: (m) => `该商品合计低于起订量（${m}）。`, step: (s) => `须按 ${s} 的倍数订购。`, over_stock: (n) => `超过可售数量（${n}）。`,
    },
    fix: '修正', priceChanged: (old) => `加入后价格已变化（原 ${old}）。`,
    summary: '订单摘要', lines: '行数', goods: '货品金额',
    goodsNote: '下一步将计算运至您地址的运费和关税，然后一次性支付总额。',
    blocking: (n) => `${n} 行需要修正数量后才能继续 — 点击该行下方的「修正」。`, dropped: (n) => `${n} 件商品暂时无货，不计入订单。可删除，或继续结算其余商品。`, next: '去结算', loading: '…',
    minimum: (min, short) => `再加 ${short} 即可完成订单`,
    suggest: '凑单推荐 — 与购物车相近的商品',
  },
};

/** Fresh catalog rows for the cart, priced and validated. */
export function useCartPricing(exchangeRates, only = null) {
  const [all, setLines] = useState(() => readCart());
  // checkout (point 2): only the lines of the option the buyer chose — the rest stay in the cart
  const lines = useMemo(() => (only ? all.filter((l) => only.has(selectionKey(l))) : all), [all, only]);
  const [data, setData] = useState(null);
  useEffect(() => onCartChange(setLines), []);
  const ids = useMemo(() => [...new Set(lines.map((l) => l.product_id))].sort().join(','), [lines]);
  const reload = useCallback(async () => {
    const r = await fetchMarketProductsByIds(ids ? ids.split(',') : []);
    setData(r);
  }, [ids]);
  useEffect(() => { reload(); }, [reload]);
  const priced = useMemo(() => (data ? priceCart(lines, data.products, data.skus, exchangeRates) : null), [lines, data, exchangeRates]);
  return { lines, priced, loading: !data, reload };
}

export default function MarketCart({ lang = 'ar', user, exchangeRates }) {
  const nav = useNavigate();
  const isAr = lang === 'ar';
  const c = CART_T[lang] || CART_T.ar;
  const font = isAr ? 'var(--font-ar)' : 'var(--font-sans)';
  usePageTitle('market', lang);
  const { lines, priced, loading } = useCartPricing(exchangeRates);
  // The minimum cart value (market_settings.min_cart_sar, owner 2026-10-29):
  // below it the cart says by how much, offers products close to what is in
  // it, and does not continue. Checkout refuses it on the server as well.
  const [minCart, setMinCart] = useState(0);
  const [minLoaded, setMinLoaded] = useState(false);
  useEffect(() => { fetchMarketState().then((v) => { setMinCart(v.min_cart_sar || 0); setMinLoaded(true); }); }, []);
  // Point 2 (owner, 2026-11-02): THREE options for every product — «أوفر» (sea), «أسرع» (air) and
  // «إكسبريس» — each with one total and its dates, priced by threeOptions.js, the very file the server
  // charges with. The carrier is chosen behind the scenes. A product with no rate for the chosen option
  // is listed separately with «اطلب سعر الشحن» and never blocks the option for the others. The cart
  // opens on the cheapest option, never on air by default. No fee line, ever.
  const dcfg = useDeliveredConfig();
  const dmap = useDeliveredMany(lines.map((l) => l.product_id), !!dcfg);
  // shipping options only while delivered_enabled is on (owner, 2026-11-02); product prices always
  const shipOff = !!dcfg && dcfg.shipping !== true;
  // the cart's sellable lines, in order — what threeOptions prices and checkout sends
  const rows = useMemo(() => (priced?.groups || []).flatMap((g) => g.lines.filter((r) => r.valid).map((r) => ({ g, r }))), [priced]);
  const loadedAll = rows.length > 0 && rows.every(({ g }) => g.product_id in dmap);
  const tl = useMemo(() => (dcfg?.shipping && loadedAll ? rows.map(({ g, r }) => {
    const info = dmap[g.product_id];
    const sku = info?.skus?.find((v) => v.key === (r.line.sku_key ?? ''));
    // no data for a line: it asks in every option — never a guess (the server does the same)
    if (!info || !sku) return { unit_sar: r.quote.unitSar, qty: r.line.qty, cls: 'general', sku: { kg: 0, cm3: 0, sea_ok: false, air_ok: false }, staff: true, refused: false };
    return { unit_sar: r.quote.unitSar, qty: r.line.qty, cls: info.air_class, sku, refused: info.partner_refused === true, staff: staffShipping(info) };
  }) : null), [dcfg, loadedAll, rows, dmap]);
  // express: the server's quote for exactly the lines express carries (kept per weight step there)
  const eIdx = useMemo(() => (tl ? expressLines(tl, dcfg) : []), [tl, dcfg]);
  const eKey = tl ? JSON.stringify(rows.map(({ r }) => [r.line.product_id, r.line.sku_key, r.line.qty])) : '';
  const [ex, setEx] = useState(null);
  useEffect(() => {
    if (!tl || !eIdx.length) { setEx(null); return undefined; }
    let alive = true;
    const t = setTimeout(async () => {
      const r = await cartExpress(rows.map(({ r: x }) => ({ product_id: x.line.product_id, sku_key: x.line.sku_key, qty: x.line.qty })));
      if (!alive) return;
      if (!r.ok) console.warn('[MarketCart] express quote refused:', r.httpStatus || '', r.error || '');
      // no answer: express asks for these lines — never an estimate
      setEx({ ...(r.ok && r.express ? r.express : { lines: eIdx, available: false }), key: eKey });
    }, 400);
    return () => { alive = false; clearTimeout(t); };
  }, [eKey]); // eslint-disable-line react-hooks/exhaustive-deps
  const three = useMemo(() => (tl ? threeOptionCart(tl, dcfg, ex && ex.key === eKey ? ex : null) : null), [tl, dcfg, ex, eKey]);
  // the tiered fee (owner, 2026-10-31): the cards price at the top rate; the cart at the rate of its value
  const ccfg = three && dcfg ? { ...dcfg, fee_rate: three.fee_rate } : dcfg;
  // the products' total (product prices: goods + fee + allowance, rounded) — every line in the cart
  const productsTotal = ccfg && priced ? Math.round((priced?.groups || []).reduce((n, g) => n + g.lines.reduce((m, r) => m + (r.valid ? productPrice(r.quote.unitSar, ccfg) * r.line.qty : 0), 0), 0) * 100) / 100 : null;
  const [method, setMethod] = useState(() => getCartMethod());
  useEffect(() => onCartChange(() => setMethod(getCartMethod())), []);
  // the cart's own names ('sea' | 'air' | 'fast') → threeOptions' modes
  const modeOf = (m) => (m === 'fast' ? 'express' : m);
  const asMethod = (mode) => (mode === 'express' ? 'fast' : mode);
  // the buyer's own pick for this cart when it can be paid, else the cheapest (threeOptions defaultMode)
  const dMethod = three ? (method && three[modeOf(method)]?.available ? method : three.default ? asMethod(three.default) : null) : null;
  const chosen = three && dMethod ? three[modeOf(dMethod)] : null;
  // nothing can be priced (every option asks for every line), or shipping switched off:
  // the whole cart is one «اطلب سعر الشحن» request — never an estimate
  const allAsk = !!three && ['sea', 'air', 'express'].every((m) => !three[m].available && !three[m].pending);
  const cartConfirm = shipOff || allAsk;
  const dt = DELIVERED_T[lang] || DELIVERED_T.ar;
  const shortBase = chosen ? chosen.total_sar : ccfg ? productsTotal : priced?.subtotalSar;
  const short = priced && shortBase != null ? cartShortfall(shortBase, minCart) : 0;
  const idxOf = useMemo(() => new Map(rows.map(({ r }, i) => [r.id, i])), [rows]);
  const askIn = (r) => !!chosen && chosen.ask.includes(idxOf.get(r.id));
  const askRows = chosen ? chosen.ask.map((i) => rows[i]) : [];
  // why a line asks in an option (store test 2026-11-03): a reason, never a bare «—»
  const whyOf = (mode, i) => (tl && dcfg ? askReason(tl[i], mode, dcfg) || 'none' : 'none');
  const whyList = (o) => [...new Set(o.ask.map((i) => dt.askWhy[whyOf(o.mode, i)] || dt.askWhy.none))].join('، ');
  const shortRows = chosen ? chosen.short.map((i) => rows[i]) : [];
  // Growth OS (step 3): the cart as the buyer first sees it, then every change.
  const lineCount = priced ? priced.groups.reduce((n, g) => n + g.lines.length, 0) : 0;
  const viewed = useRef(false);
  useEffect(() => {
    if (viewed.current || !priced || !minLoaded || shortBase == null) return;
    viewed.current = true;
    // An empty cart shows «السلة فارغة», never the minimum, so it is never
    // "below the minimum" either (seen 2026-11-01: an empty cart was recorded
    // as stopped at the 150 ر.س minimum).
    const below = lineCount > 0 && short > 0;
    track('cart_viewed', { lines: lineCount, goods_sar: priced.subtotalSar, total_sar: shortBase, below_minimum: below });
    if (below) track('cart_minimum_blocked', { total_sar: shortBase, gap_sar: short });
  }, [priced, minLoaded, shortBase, short, lineCount]);
  const qtyBefore = useRef({});
  const lineQty = (r, next) => {
    const after = Math.max(0, Math.floor(Number(next) || 0));
    if (after !== r.line.qty) track('cart_item_qty_changed', { product_id: r.line.product_id, sku_key: r.line.sku_key, qty_before: r.line.qty, qty_after: after, cart_total_sar: shortBase });
    setLineQty(r.id, next);
  };
  const removeRow = (r) => {
    track('cart_item_removed', { product_id: r.line.product_id, sku_key: r.line.sku_key, qty: r.line.qty, line_sar: r.quote?.totalSar, cart_total_sar: shortBase });
    removeLine(r.id);
  };
  const pickMethod = (m) => {
    const to = three?.[modeOf(m)];
    if (m !== dMethod) track('cart_shipping_changed', { from_option: dMethod, to_option: m, price_diff_sar: to?.total_sar != null && chosen?.total_sar != null ? Math.round((to.total_sar - chosen.total_sar) * 100) / 100 : undefined });
    setCartMethod(m);
  };
  // a line's product price (goods + fee + allowance at the cart's tier, rounded) — no shipping
  const lineProduct = (r) => (r.valid && ccfg ? productPrice(r.quote.unitSar, ccfg) : r.quote?.unitSar);
  // a line at its product price (shipping not in it): the shown unit × qty, as the server charges it
  const productLine = (r) => (ccfg ? Math.round(productPrice(r.quote.unitSar, ccfg) * r.line.qty * 100) / 100 : r.quote.totalSar);
  // the landed price per piece — only where it is the partner's own sea/air price (the other carriers
  // are priced for their group, not per piece); null elsewhere
  const landed = (r) => {
    const i = idxOf.get(r.id);
    if (!chosen || dMethod === 'fast' || i == null || !chosen.groups.some((gr) => gr.carrier === 'partner' && gr.lines.includes(i))) return null;
    const d = deliveredUnit(r.quote.unitSar, tl[i].sku, tl[i].cls, r.line.qty, ccfg);
    return d[dMethod] ?? null;
  };
  const lineUnit = (g, r) => landed(r) ?? lineProduct(r);
  // "was X": the old factory price as a product price
  const oldShown = (g, r) => (r.oldUnitSar != null && ccfg ? productPrice(r.oldUnitSar, ccfg) : r.oldUnitSar);
  // the line's own total: landed (partner sea/air, rounded once) or its product price
  const lineTotal = (g, r) => {
    const i = idxOf.get(r.id);
    const t = landed(r) != null ? deliveredLineTotal(r.quote.unitSar, tl[i].sku, tl[i].cls, r.line.qty, ccfg, dMethod) : null;
    return t ?? productLine(r);
  };
  // to checkout: the chosen option with ONLY the lines it carries; or «اطلب سعر الشحن» for given lines
  const goCheckout = (sel) => {
    track('checkout_started', { total_sar: shortBase, lines: lineCount, shipping_option: sel.ask ? 'ask' : dMethod, signed_in: !!user });
    setCheckoutSelection(sel);
    nav('/market/checkout');
  };
  const keysOf = (rs) => rs.map(({ r }) => selectionKey(r.line));
  const mainId = useMemo(() => {
    const value = (g) => g.lines.reduce((n, r) => n + Number(r.quote?.totalSar ?? 0), 0);
    const g = (priced?.groups || []).filter((x) => x.product).reduce((best, x) => (!best || value(x) > value(best) ? x : best), null);
    return g?.product?.id || lines[0]?.product_id || null;
  }, [priced, lines]);
  const [suggest, setSuggest] = useState([]);
  useEffect(() => {
    if (!short || !mainId) { setSuggest([]); return undefined; }
    let alive = true;
    fetchRelated(mainId).then((r) => {
      if (!alive) return;
      const inCart = new Set(lines.map((l) => l.product_id));
      const seen = new Set();
      setSuggest([...r.sameSeller, ...r.related].filter((p) => !inCart.has(p.id) && !seen.has(p.id) && seen.add(p.id)).slice(0, 8));
    });
    return () => { alive = false; };
  }, [short > 0, mainId]); // eslint-disable-line react-hooks/exhaustive-deps

  const issueText = (code, row, g) => {
    const f = c.issues[code];
    if (typeof f !== 'function') return f;
    if (code === 'below_moq') return f(g.productMin);
    if (code === 'step') return f(row.quote.step);
    return f(Number(row.sku?.stock ?? 0).toLocaleString('en-US'));
  };
  // The nearest valid quantity for a line: a multiple of the step, within
  // stock, and — when the product's total is short of its minimum — raised by
  // the shortfall (the minimum counts every variant of the product).
  const fixQty = (row, g) => {
    const { step } = row.quote;
    const stock = Number(row.sku?.stock ?? Infinity);
    const need = row.line.qty + Math.max(0, g.productMin - g.productQty);
    const up = Math.ceil(need / step) * step;
    return up <= stock ? up : Math.floor(stock / step) * step;
  };

  return (
    <MarketShell lang={lang} user={user}>
      <div className="fx-wrap">
        {MARKET_API_IS_LOCAL && <PreviewBanner text={c.preview} isAr={isAr} />}
        <button className={`fx-back${isAr ? ' ar' : ''}`} onClick={() => nav('/market')}>{isAr ? '→ ' : '← '}{c.back}</button>
        <h1 className={`fx-h1${isAr ? ' ar' : ''}`} style={{ fontSize: isAr ? 24 : 27, margin: '6px 0 18px' }}>{c.title}</h1>

        {!lines.length ? (
          <div style={{ padding: 30, textAlign: 'center', border: '1px dashed var(--border)', borderRadius: 16, fontFamily: font }}>
            <p style={{ color: 'var(--text-secondary)' }}>{c.empty}</p>
            <button type="button" onClick={() => nav('/market')} style={{ padding: '9px 18px', borderRadius: 12, border: '1px solid var(--border-strong)', background: 'var(--surface-raised)', cursor: 'pointer', fontFamily: font }}>{c.browse}</button>
          </div>
        ) : loading || !priced ? (
          <p style={{ color: 'var(--text-secondary)' }}>{c.loading}</p>
        ) : (
          <div style={{ display: 'flex', gap: 28, flexWrap: 'wrap', alignItems: 'flex-start' }}>
            <div style={{ flex: '1 1 560px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 14 }}>
              {priced.groups.map((g) => {
                const p = g.product;
                const tierByTotal = g.lines.length > 1 && g.lines.some((r) => r.quote?.mode === 'quantity');
                return (
                  <div key={g.product_id} data-cart-group={g.product_id} style={{ border: '1px solid var(--border)', borderRadius: 16, background: 'var(--surface-raised)', padding: 14 }}>
                    <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 10 }}>
                      {p?.images?.[0]?.ref && <img src={marketImageUrl(p.images[0].ref, true)} alt="" style={{ width: 56, height: 56, borderRadius: 10, objectFit: 'cover', flex: '0 0 auto' }} />}
                      <button type="button" onClick={() => p && nav(`/market/${p.id}`)} style={{ background: 'none', border: 'none', padding: 0, textAlign: isAr ? 'right' : 'left', cursor: p ? 'pointer' : 'default', fontFamily: font, fontSize: 14.5, fontWeight: 600, color: 'var(--text-primary)', lineHeight: 1.5 }}>
                        {p ? marketTitle(p, lang) : '—'}
                        {three && p?.seller_name_latin && <SupplierName className="mk-line-seller" name={p.seller_name_latin} prefix="✓ " />}
                      </button>
                    </div>
                    {tierByTotal && <p data-tier-by-total={g.productQty} style={{ margin: '0 0 8px', fontSize: 12, color: '#7A5A22', fontFamily: font }}>{c.tierByTotal(g.productQty.toLocaleString('en-US'))}</p>}
                    {g.lines.map((r) => {
                      const step = r.quote?.step || 1;
                      return (
                        <div key={r.id} data-cart-line={r.id} data-line-valid={r.valid ? '1' : '0'} style={{ borderTop: '1px solid var(--border)', padding: '10px 0 4px', display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center', justifyContent: 'space-between' }}>
                          <div style={{ minWidth: 160, flex: '1 1 200px', fontFamily: font }}>
                            <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{variantText(p, r.sku, lang) || '—'}</div>
                            {ccfg && r.valid ? (
                              <>
                                {/* the product price, then the landed price per piece (owner, 2026-11-02): shipping shows in the cart */}
                                <div className="mk-line-dprice" data-line-product={lineProduct(r) ?? ''}>
                                  <span>{dt.productLabel}</span>
                                  <strong>{formatSar(lineProduct(r))}</strong>
                                </div>
                                {landed(r) != null && (
                                  <div className="mk-line-dprice" data-line-delivered="1">
                                    <span>{dt.perPiece}</span>
                                    <strong data-line-unit={lineUnit(g, r) ?? ''}>{formatSar(lineUnit(g, r))}</strong>
                                  </div>
                                )}
                                {/* no rate for the chosen option (point 2): listed in the summary with «اطلب سعر الشحن» */}
                                {askIn(r) && <div className="mk-line-ask" data-line-ask={chosen.mode}>{dt.askFor[chosen.mode]} · {dt.askWhy[whyOf(chosen.mode, idxOf.get(r.id))] || dt.askWhy.none}</div>}
                              </>
                            ) : (
                              <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{c.unit}: <span data-line-unit={lineUnit(g, r) ?? ''}>{formatSar(lineUnit(g, r)) || '—'}</span></div>
                            )}
                          </div>
                          <div style={{ display: 'inline-flex', border: '1px solid var(--border-strong)', borderRadius: 10, overflow: 'hidden' }} dir="ltr">
                            <button type="button" aria-label={c.dec} data-line-dec="1" disabled={r.line.qty - step < step}
                              onClick={() => lineQty(r, r.line.qty - step)} style={{ width: 32, border: 'none', background: 'var(--bg-hero)', cursor: 'pointer', fontSize: 16 }}>−</button>
                            <input aria-label={c.qty} data-line-qty="1" type="number" value={r.line.qty} min={step} step={step} inputMode="numeric"
                              onFocus={() => { qtyBefore.current[r.id] = r.line.qty; }}
                              onBlur={() => { const b = qtyBefore.current[r.id]; if (b != null && b !== r.line.qty) track('cart_item_qty_changed', { product_id: r.line.product_id, sku_key: r.line.sku_key, qty_before: b, qty_after: r.line.qty, cart_total_sar: shortBase }); delete qtyBefore.current[r.id]; }}
                              onChange={(e) => setLineQty(r.id, e.target.value)}
                              style={{ width: 80, border: 'none', borderInline: '1px solid var(--border)', textAlign: 'center', fontSize: 14, padding: '6px 4px' }} />
                            <button type="button" aria-label={c.inc} data-line-inc="1"
                              onClick={() => lineQty(r, r.line.qty + step)} style={{ width: 32, border: 'none', background: 'var(--bg-hero)', cursor: 'pointer', fontSize: 16 }}>+</button>
                          </div>
                          <div style={{ minWidth: 110, textAlign: isAr ? 'left' : 'right', fontFamily: font }}>
                            <div style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>{c.lineTotal}</div>
                            <strong data-line-total={r.valid ? lineTotal(g, r) : ''} style={{ fontSize: three ? 13 : 14.5, color: r.valid ? 'var(--text-primary)' : 'var(--text-muted)', textDecoration: r.valid ? 'none' : 'line-through' }}>{formatSar(r.valid ? lineTotal(g, r) : r.quote?.totalSar) || '—'}</strong>
                          </div>
                          <button type="button" data-line-remove="1" onClick={() => removeRow(r)} style={{ background: 'none', border: 'none', color: '#B3261E', cursor: 'pointer', fontSize: 12.5, fontFamily: font }}>{c.remove}</button>
                          {(r.issues.length > 0 || r.priceChanged) && (
                            <div style={{ flexBasis: '100%', fontSize: 12.5, fontFamily: font }}>
                              {r.issues.map((code) => (
                                <span key={code} data-line-issue={code} style={{ color: '#B3261E', marginInlineEnd: 10 }}>{issueText(code, r, g)}</span>
                              ))}
                              {r.issues.some((x) => ['below_moq', 'step', 'over_stock'].includes(x)) && fixQty(r, g) >= step && fixQty(r, g) !== r.line.qty && (
                                <button type="button" data-line-fix={fixQty(r, g)} onClick={() => lineQty(r, fixQty(r, g))} style={{ background: 'none', border: 'none', padding: 0, color: '#9A7636', textDecoration: 'underline', cursor: 'pointer', fontFamily: font, fontSize: 12.5 }}>{c.fix} → {fixQty(r, g).toLocaleString('en-US')}</button>
                              )}
                              {r.priceChanged && <span data-price-changed="1" style={{ color: '#7A5A22' }}>{c.priceChanged(formatSar(oldShown(g, r)))}</span>}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>

            <aside style={{ flex: '0 1 320px', minWidth: 260, border: '1px solid var(--border)', borderRadius: 16, padding: 16, background: 'var(--surface-raised)', fontFamily: font, position: 'sticky', top: 90 }}>
              <h2 style={{ fontSize: 16, margin: '0 0 12px' }}>{c.summary}</h2>
              <Row k={c.lines} v={lines.length} />
              {cartConfirm ? (
                <>
                  <Row k={ccfg ? dt.productsTotal : c.goods} v={<strong data-cart-subtotal={productsTotal ?? priced.subtotalSar} data-cart-products={productsTotal ?? undefined}>{formatSar(productsTotal ?? priced.subtotalSar)}</strong>} />
                  <div data-cart-confirm="1" style={{ fontSize: 13, lineHeight: 1.7, padding: '10px 12px', borderRadius: 10, background: '#EEF2F8', color: '#2B4A7A', margin: '0 0 10px' }}>
                    {dt.confirmAllCart(dcfg?.confirm_hours ?? 48)}
                  </div>
                </>
              ) : three ? (
                <>
                  {/* three options, each one total and its dates (owner, 2026-11-02, point 2) */}
                  <div data-cart-methods={dMethod || ''} className="mk-cart-methods">
                    {['sea', 'air', 'fast'].map((m) => {
                      const o = three[modeOf(m)];
                      const on = dMethod === m;
                      const save = m === 'sea' && o.available && three.air.available && o.lines.length === three.air.lines.length
                        ? Math.round((three.air.total_sar - o.total_sar) * 100) / 100 : 0;
                      return (
                        <button key={m} type="button" className={`mk-cart-method${on ? ' on' : ''}${m === 'sea' ? ' cheap' : ''}`} data-cart-method={m}
                          data-cart-method-on={on ? '1' : '0'} disabled={!o.available} onClick={() => pickMethod(m)}>
                          <span className="mk-cart-method-head"><b>{m === 'air' ? dt.fast : m === 'sea' ? dt.cheap : dt.express}</b>{save > 0 && <span className="mk-save-badge" data-cart-save={save}>{dt.save(formatSar(save))}</span>}</span>
                          {o.available ? (
                            <>
                              <span className="mk-cart-method-total" data-cart-method-total={o.total_sar}>{formatSar(o.total_sar)}</span>
                              <span className="mk-cart-method-meta">{dt.arrives(shortDate(daysFromNow(o.days_min), lang), shortDate(daysFromNow(o.days_max), lang))}</span>
                              {/* what this total covers (store test 2026-11-03): «يشمل 2 من 3 منتجات» */}
                              {o.lines.length < rows.length && <span className="mk-cart-method-meta" data-cart-method-covers={`${o.lines.length}/${rows.length}`}>{dt.covers(o.lines.length, rows.length)}</span>}
                            </>
                          ) : (
                            <span className="mk-cart-method-meta" data-cart-method-why={o.pending ? 'pending' : o.short.length ? 'short' : 'ask'} data-cart-sea-add={o.add_units || ''}>
                              {o.pending ? dt.pricing : o.short.length && o.add_units > 0 ? dt.addForSea(o.add_units.toLocaleString('en-US')) : `${dt.askFor[o.mode]} · ${whyList(o)}`}
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                  {chosen && (
                    <>
                      <Row k={dt.productsTotal} v={<strong data-cart-products={chosen.products_sar}>{formatSar(chosen.products_sar)}</strong>} />
                      <Row k={dt.shipCustoms} v={<strong data-cart-ship-customs={chosen.shipping_sar}>{formatSar(chosen.shipping_sar)}</strong>} />
                      <div className="mk-cart-hero" data-cart-hero={chosen.total_sar}>
                        <div className="mk-cart-hero-total"><span>{dt.total}</span><b data-cart-total={chosen.total_sar}>{formatSar(chosen.total_sar)}</b></div>
                      </div>
                      {three.fee_discount_sar > 0 && (
                        <Row k={dt.feeDiscount} v={<strong data-fee-discount={three.fee_discount_sar} style={{ color: '#2E7D32' }}>{dt.feeSaved(formatSar(three.fee_discount_sar))}</strong>} />
                      )}
                      {three.next_fee_tier && (
                        <p className="mk-fee-nudge" data-fee-nudge={three.next_fee_tier.need_sar} style={{ fontSize: 12.5, color: '#9A7636', margin: '6px 0' }}>{dt.feeNudge(formatSar(three.next_fee_tier.need_sar))}</p>
                      )}
                    </>
                  )}
                  {/* no rate for the chosen option: listed separately, one request without payment — never an estimate */}
                  {askRows.length > 0 && (
                    <div className="mk-cart-ask" data-cart-ask={askRows.length}>
                      <p>{dt.askHead}</p>
                      <ul>{askRows.map(({ g, r }) => <li key={r.id}>{g.product ? marketTitle(g.product, lang) : '—'} · <span dir="ltr">{r.line.qty.toLocaleString('en-US')}</span></li>)}</ul>
                      <button type="button" data-cart-ask-request="1" onClick={() => goCheckout({ method: dMethod, keys: keysOf(askRows), ask: true })}>{dt.askFor[chosen.mode] || dt.confirmAsk}</button>
                    </div>
                  )}
                  {shortRows.length > 0 && (
                    <p className="mk-cart-short" data-cart-short={shortRows.length}>{dt.seaShortHead(chosen?.add_units || three.sea.add_units || 0)}</p>
                  )}
                  <p className="mk-trust-line">{dt.finalPrice}</p>
                </>
              ) : (
                <>
                  <Row k={ccfg ? dt.productsTotal : c.goods} v={<strong data-cart-subtotal={productsTotal ?? priced.subtotalSar}>{formatSar(productsTotal ?? priced.subtotalSar)}</strong>} />
                  <p style={{ fontSize: 11.5, color: 'var(--text-muted)', margin: '8px 0 12px', lineHeight: 1.6 }}>{dcfg ? dt.pricing : c.goodsNote}</p>
                </>
              )}
              {priced.fixable > 0 && <p data-cart-blocking={priced.fixable} style={{ fontSize: 12.5, color: '#B3261E', margin: '0 0 10px' }}>{c.blocking(priced.fixable)}</p>}
              {priced.dropped > 0 && <p data-cart-dropped={priced.dropped} style={{ fontSize: 12.5, color: '#7A5A22', margin: '0 0 10px' }}>{c.dropped(priced.dropped)}</p>}
              {short > 0 && (
                <div className="mk-min-progress" data-cart-minimum={short}>
                  <div className="mk-min-bar"><span style={{ width: `${Math.max(4, Math.min(100, ((minCart - short) / minCart) * 100))}%` }} /></div>
                  <p>{c.minimum(formatSar(minCart), formatSar(short))}</p>
                </div>
              )}
              {(() => {
                // the chosen option's own lines go to checkout; the whole cart when it is one price request
                const can = priced.sellable && !short && (cartConfirm || !!chosen);
                return (
                  <button type="button" data-cart-next="1" disabled={!can}
                    onClick={() => (cartConfirm ? goCheckout({ method: null, keys: keysOf(rows), ask: true })
                      : goCheckout({ method: dMethod, keys: keysOf(chosen.lines.map((i) => rows[i])), ask: false,
                        // what the cart showed for this option — checkout shows it at once, then confirms it (store test 2026-11-03)
                        preview: { products_sar: chosen.products_sar, shipping_sar: chosen.shipping_sar, total_sar: chosen.total_sar, days_min: chosen.days_min, days_max: chosen.days_max } }))}
                    style={{ width: '100%', padding: '11px 0', borderRadius: 12, border: 'none', fontSize: 14, fontWeight: 600, fontFamily: font,
                      background: can ? '#1A1814' : 'var(--border)', color: can ? '#fff' : 'var(--text-muted)', cursor: can ? 'pointer' : 'not-allowed' }}>{cartConfirm ? dt.confirmAsk : c.next}</button>
                );
              })()}
            </aside>
          </div>
        )}
        {short > 0 && suggest.length > 0 && (
          <div data-cart-suggestions={suggest.length} style={{ marginTop: 26 }}>
            <h2 style={{ fontSize: 17, fontFamily: font, margin: '0 0 8px' }}>{c.suggest}</h2>
            <CardRow rows={suggest} lang={lang} user={user} exchangeRates={exchangeRates} max={8} strip />
          </div>
        )}
      </div>
    </MarketShell>
  );
}

/** A date `days` from today (the option's own transit days). */
function daysFromNow(days) { const d = new Date(); d.setDate(d.getDate() + Number(days || 0)); return d; }

export function Row({ k, v, muted }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '5px 0', fontSize: 13.5, color: muted ? 'var(--text-muted)' : 'var(--text-secondary)' }}>
      <span>{k}</span><span>{v}</span>
    </div>
  );
}
